import { useEffect, useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Link, NavLink, Outlet, useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  AppShell,
  BrandMark,
  endSession,
  SIGN_OUT_FAILED_MESSAGE,
  HelpTip,
  Icon,
  cn,
  containerClass,
  navLinkClass,
  type NavVariant,
} from '@dataroom/shared';
import { useRoomExclusivity, useSession } from '@/api/hooks';
import { isSellerTeamOnly, loiAccess, sellerRooms } from '@/config';
import { contractsEnabled } from '@/lib/features';

/**
 * The seller portal chrome — the shared AppShell (one header and 1200px
 * container for all three apps; nav collapses to a drawer under 768px) with
 * "Your rooms" and Sign out. Used by the rooms landing and every room view.
 */
export function PortalFrame({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const qc = useQueryClient();

  function signOut() {
    void endSession(qc).then((ended) => {
      if (ended) navigate('/login');
      else window.alert(SIGN_OUT_FAILED_MESSAGE);
    });
  }

  return (
    <AppShell
      brand={
        <Link to="/" aria-label="ClosingVault Seller — your rooms" className="rounded-md">
          <BrandMark appLabel="Seller" />
        </Link>
      }
      renderNav={(variant: NavVariant, close) => (
        <NavLink
          to="/"
          end
          onClick={close}
          className={({ isActive }) => navLinkClass(variant, isActive)}
        >
          Your rooms
        </NavLink>
      )}
      renderActions={(variant: NavVariant, close) => (
        <>
          <NavLink
            to="/help"
            onClick={close}
            className={({ isActive }) => navLinkClass(variant, isActive)}
          >
            <Icon name="help" />
            Help
          </NavLink>
          <button
            type="button"
            className={navLinkClass(variant, false)}
            onClick={() => {
              close();
              signOut();
            }}
          >
            Sign out
          </button>
        </>
      )}
    >
      {children}
    </AppShell>
  );
}

/** Marks a room the caller works on as a member of the seller's team rather
 * than as the seller. */
export function SellerTeamLabel() {
  return (
    <span className="inline-flex shrink-0 items-center rounded-full border px-2 py-0.5 text-xs font-medium text-ink-2">
      Seller&apos;s team
    </span>
  );
}

/**
 * Chrome for a room-scoped seller view (mounted at /rooms/:roomId): a room
 * header — "Your rooms" back link, the room name as the page's h1, and the
 * room's sections as tabs that scroll sideways instead of wrapping — over the
 * section's page. A seller in more than one deal is never stranded in one room.
 */
export function Layout() {
  const { roomId } = useParams<{ roomId: string }>();
  const session = useSession();
  const location = useLocation();
  const room = sellerRooms(session.data).find((r) => r.roomId === roomId);
  const roomName = room?.name;
  // A seller's-team member works here with the seller's access. Letters of
  // intent: the sellers vote on them; a team member who is Legal may read them
  // and their outcome; any other team member sees no LOIs (the API answers 404).
  const teamOnly = isSellerTeamOnly(room);
  const lois = loiAccess(room);
  const base = `/rooms/${roomId ?? ''}`;
  // An accepted LOI or an extension waiting for the sellers' vote is an in-app
  // item on every seller page, linking to the LOIs tab. Asked only once the
  // session shows the caller is a seller, and shown only to a seller who may
  // vote now (the vote carries `viewerCanVote`).
  const exclusivity = useRoomExclusivity(room?.isSeller ? roomId : undefined);
  const sellerVote = exclusivity.data?.sellerVote ?? null;
  const needsVote = !exclusivity.data
    ? null
    : sellerVote
      ? !sellerVote.viewerCanVote
        ? null
        : sellerVote.kind === 'acceptance'
          ? 'An accepted LOI is waiting for your vote.'
          : sellerVote.kind === 'extension'
            ? 'An exclusivity extension is waiting for your vote.'
            : null
      : // An older server (no vote data): the single pinned validator.
        !exclusivity.data.viewerIsValidator
        ? null
        : exclusivity.data.state === 'pending_validation'
          ? 'An accepted LOI is waiting for your validation.'
          : exclusivity.data.exclusivity?.pendingExtensionDays != null
            ? 'An exclusivity extension is waiting for your validation.'
            : null;
  const tabs = [
    { to: base, label: 'Dashboard', end: true },
    { to: `${base}/documents`, label: 'Documents' },
    { to: `${base}/processing`, label: 'Processing' },
    { to: `${base}/checklist`, label: 'Checklist' },
    ...(lois === 'none' ? [] : [{ to: `${base}/lois`, label: 'LOIs' }]),
    { to: `${base}/questions`, label: 'Questions for you' },
    { to: `${base}/risks`, label: 'Risks & timeline' },
    ...(contractsEnabled() ? [{ to: `${base}/contracts`, label: 'Contracts' }] : []),
  ];

  const tabsRef = useRef<HTMLElement>(null);
  useEffect(() => {
    tabsRef.current
      ?.querySelector<HTMLElement>('[aria-current="page"]')
      ?.scrollIntoView?.({ block: 'nearest', inline: 'nearest' });
  }, [location.pathname]);

  return (
    <PortalFrame>
      <div className={cn(containerClass, 'flex flex-col gap-2 pt-6')}>
        <Link
          to="/"
          className="inline-flex w-fit items-center gap-1.5 text-[13px] text-ink-2 hover:underline"
        >
          <Icon name="arrowLeft" size={14} />
          Your rooms
        </Link>
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          <h1 className="break-words font-serif text-2xl font-semibold leading-8 tracking-tight text-balance md:text-[28px] md:leading-9">
            {roomName ?? 'Your data room'}
          </h1>
          {teamOnly ? (
            <span className="inline-flex items-center gap-1">
              <SellerTeamLabel />
              <HelpTip id="seller.rooms.team-label" />
            </span>
          ) : null}
        </div>
        <nav ref={tabsRef} aria-label="Room sections" className="dr-scroll-x mt-2 border-b">
          <ul className="flex min-w-max gap-1 pr-6">
            {tabs.map((tab) => (
              <li key={tab.to}>
                <NavLink
                  to={tab.to}
                  end={tab.end}
                  className={({ isActive }) =>
                    cn(
                      'relative inline-flex h-11 items-center whitespace-nowrap px-3 text-sm transition-colors',
                      isActive
                        ? 'font-semibold text-ink after:absolute after:inset-x-2 after:-bottom-px after:h-0.5 after:rounded-full after:bg-primary'
                        : 'font-medium text-ink-2 hover:text-ink',
                    )
                  }
                >
                  {tab.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        {needsVote && !location.pathname.endsWith('/lois') ? (
          <div
            role="status"
            className="mt-2 flex flex-wrap items-center justify-between gap-2 rounded-md border border-st-check-line bg-st-check px-3 py-2 text-sm text-st-check-fg"
          >
            <span className="font-medium">{needsVote}</span>
            <Link to={`${base}/lois`} className="font-semibold underline">
              Review it
            </Link>
          </div>
        ) : null}
      </div>
      <main>
        <Outlet />
      </main>
    </PortalFrame>
  );
}
